import {
  type Condition,
  type Configuration,
  type CreateRestoreTemplateRequest,
  type RestoreTemplateFilterConditions,
  type RestoreTemplateResourceType,
  type RevalidateRestoreTemplateRequest,
  type UpdateRestoreTemplateRequest,
  ConditionOperatorEnum,
  RestoreTemplatesApi,
} from '@repo/api-gw-sdk';

import { convertQuery } from '@/utils/conditionQuery';
import { encodePageToken } from '@/utils/string';

import type { HttpClient } from './httpClient';

const baseUrl = '/restore-templates';

export const buildResourceTypeCondition = (
  resourceType: RestoreTemplateResourceType | undefined
): Condition | undefined => {
  if (!resourceType) {
    return undefined;
  }
  return {
    operator: ConditionOperatorEnum.And,
    conditions: [
      {
        property: 'resourceType',
        operator: ConditionOperatorEnum.In,
        value: [resourceType],
      },
    ],
  };
};

// Revalidating with the snapshot the restore will use checks what the restore launches: an inheriting template takes
// the instance type recorded in that snapshot, which can differ from the resource's current type.
export const buildRevalidateRequest = (
  resourceId?: string,
  snapshotId?: string
): RevalidateRestoreTemplateRequest | undefined => {
  if (!resourceId && !snapshotId) {
    return undefined;
  }
  return {
    ...(resourceId ? { resourceId } : {}),
    ...(snapshotId ? { snapshotId } : {}),
  };
};

export const restoreTemplates = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const restoreTemplatesApi = new RestoreTemplatesApi(config);

  return {
    useList: ({
      condition,
      enabled = true,
      pageIndex,
      pageSize,
    }: {
      condition?: Condition;
      enabled?: boolean;
      pageIndex?: number;
      pageSize?: number;
    } = {}) => {
      const filters = convertQuery<RestoreTemplateFilterConditions>(condition);
      const pageToken =
        typeof pageIndex === 'number' && pageIndex > 0
          ? encodePageToken(pageIndex)
          : undefined;

      return httpClient.execute(
        restoreTemplatesApi.listRestoreTemplatesV2.bind(restoreTemplatesApi),
        enabled ? baseUrl : null,
        'listRestoreTemplates',
        currentProjectId,
        pageToken,
        pageSize,
        filters ? { filters } : undefined
      );
    },
    useGet: (restoreTemplateId: string | null) =>
      httpClient.execute(
        restoreTemplatesApi.getRestoreTemplate.bind(restoreTemplatesApi),
        restoreTemplateId ? baseUrl : null,
        restoreTemplateId ?? '',
        currentProjectId,
        restoreTemplateId ?? ''
      ),
    create: (body: CreateRestoreTemplateRequest) =>
      restoreTemplatesApi.createRestoreTemplate(currentProjectId, body),
    update: (restoreTemplateId: string, body: UpdateRestoreTemplateRequest) =>
      restoreTemplatesApi.updateRestoreTemplate(
        currentProjectId,
        restoreTemplateId,
        body
      ),
    delete: (restoreTemplateId: string) =>
      restoreTemplatesApi.deleteRestoreTemplate(
        currentProjectId,
        restoreTemplateId
      ),
    bulkAttach: (restoreTemplateId: string, resourceIds: string[]) =>
      restoreTemplatesApi.bulkAttachRestoreTemplate(
        currentProjectId,
        restoreTemplateId,
        { resourceIds }
      ),
    bulkDetach: (restoreTemplateId: string, resourceIds: string[]) =>
      restoreTemplatesApi.bulkDetachRestoreTemplate(
        currentProjectId,
        restoreTemplateId,
        { resourceIds }
      ),
    useRevalidate: (
      restoreTemplateId: string,
      resourceId?: string,
      snapshotId?: string
    ) =>
      httpClient.execute(
        restoreTemplatesApi.revalidateRestoreTemplate.bind(restoreTemplatesApi),
        restoreTemplateId ? baseUrl : null,
        'revalidate',
        currentProjectId,
        restoreTemplateId,
        buildRevalidateRequest(resourceId, snapshotId)
      ),
    useEvaluate: (
      restoreTemplateId: string,
      resourceId: string,
      snapshotId: string
    ) =>
      httpClient.execute(
        restoreTemplatesApi.evaluateRestoreTemplate.bind(restoreTemplatesApi),
        restoreTemplateId ? baseUrl : null,
        'evaluate',
        currentProjectId,
        restoreTemplateId,
        resourceId,
        snapshotId
      ),
  };
};
